import React, { useState } from 'react';
import { Link } from 'react-router-dom';

const DentalAutomationSection = () => {
  const [hoveredCard, setHoveredCard] = useState(null);

  const automationProcesses = [
    {
      id: 'EV',
      title: 'Eligibility Verification',
      description: 'Automatically verify patient insurance eligibility in real-time before appointments. This bot checks coverage details, benefits, deductibles, and co-pays across multiple insurance providers, reducing claim denials and improving patient experience.',
      color: '#10b981',
      bgColor: 'rgba(16, 185, 129, 0.1)',
      borderColor: 'rgba(16, 185, 129, 0.3)'
    },
    {
      id: 'PA',
      title: 'Prior Authorization',
      description: 'Streamline prior authorization requests for dental procedures. The bot automatically submits PA requests with required documentation, tracks approval status, and notifies staff of decisions, reducing treatment delays.',
      color: '#3b82f6',
      bgColor: 'rgba(59, 130, 246, 0.1)',
      borderColor: 'rgba(59, 130, 246, 0.3)'
    },
    {
      id: 'CF',
      title: 'Claims Filing',
      description: 'Automate dental claims submission with accurate coding and documentation. The bot validates claim data, attaches necessary files, submits to appropriate payers, and tracks submission status for faster reimbursements.',
      color: '#8b5cf6',
      bgColor: 'rgba(139, 92, 246, 0.1)',
      borderColor: 'rgba(139, 92, 246, 0.3)'
    },
    {
      id: 'RP',
      title: 'Revenue Posting',
      description: 'Automatically post insurance payments and patient payments to the correct accounts. The bot reconciles ERAs, posts payments, adjustments, and denials while maintaining accurate financial records.',
      color: '#f97316',
      bgColor: 'rgba(249, 115, 22, 0.1)',
      borderColor: 'rgba(249, 115, 22, 0.3)'
    },
    {
      id: 'DM',
      title: 'Denial Management',
      description: 'Intelligently handle claim denials and rejections. The bot analyzes denial reasons, determines if claims can be corrected and resubmitted, or if appeals are necessary, maximizing revenue recovery.',
      color: '#ef4444',
      bgColor: 'rgba(239, 68, 68, 0.1)',
      borderColor: 'rgba(239, 68, 68, 0.3)'
    },
    {
      id: 'AR',
      title: 'Accounts Receivable',
      description: 'Automate A/R follow-up and aging management. The bot identifies overdue accounts, generates follow-up letters, makes collection calls, and tracks payment plans to optimize cash flow.',
      color: '#06b6d4',
      bgColor: 'rgba(6, 182, 212, 0.1)',
      borderColor: 'rgba(6, 182, 212, 0.3)'
    },
    {
      id: 'PP',
      title: 'Patient Payments',
      description: 'Streamline patient payment collection with automated payment reminders, online payment processing, and payment plan management. Improve patient satisfaction while reducing outstanding balances.',
      color: '#ec4899',
      bgColor: 'rgba(236, 72, 153, 0.1)',
      borderColor: 'rgba(236, 72, 153, 0.3)'
    },
    {
      id: 'IC',
      title: 'Insurance Coordination',
      description: 'Coordinate benefits between primary and secondary insurance plans. The bot automatically sequences claims, calculates coordination of benefits, and ensures maximum reimbursement from all payers.',
      color: '#84cc16',
      bgColor: 'rgba(132, 204, 22, 0.1)',
      borderColor: 'rgba(132, 204, 22, 0.3)'
    },
    {
      id: 'CR',
      title: 'Compliance Reporting',
      description: 'Generate automated compliance reports for dental practices. The bot creates audit trails, tracks key performance indicators, and ensures adherence to healthcare regulations and billing standards.',
      color: '#a855f7',
      bgColor: 'rgba(168, 85, 247, 0.1)',
      borderColor: 'rgba(168, 85, 247, 0.3)'
    }
  ];

  const containerStyle = {
    width: '100%',
    minHeight: '100vh',
    background: '#ffffff',
    fontFamily: 'system-ui, -apple-system, sans-serif',
    padding: '0'
  };

  const innerContainerStyle = {
    maxWidth: '1400px',
    margin: '0 auto',
    padding: '4rem 2rem',
  };

  const headerStyle = {
    textAlign: 'center',
    marginBottom: '3rem'
  };

  const titleStyle = {
    fontSize: 'clamp(2rem, 4vw, 2.5rem)',
    fontWeight: '700',
    color: '#111827',
    marginBottom: '1rem',
    lineHeight: '1.2'
  };

  const subtitleStyle = {
    fontSize: '1.125rem',
    color: '#6b7280',
    maxWidth: '900px',
    margin: '0 auto 1.5rem',
    lineHeight: '1.6'
  };

  const getCardStyle = (index) => ({
    background: 'rgba(248, 250, 252, 0.8)',
    backdropFilter: 'blur(10px)',
    borderRadius: '1rem',
    border: `1px solid ${automationProcesses[index].borderColor}`,
    padding: '1.5rem',
    cursor: 'pointer',
    transition: 'all 0.3s ease',
    transform: hoveredCard === index ? 'translateY(-8px) scale(1.02)' : 'translateY(0) scale(1)',
    boxShadow: hoveredCard === index 
      ? '0 20px 40px rgba(0, 0, 0, 0.15)' 
      : '0 10px 25px rgba(0, 0, 0, 0.08)',
    position: 'relative',
    overflow: 'hidden'
  });

  const cardHeaderStyle = (process) => ({
    display: 'flex',
    alignItems: 'center',
    gap: '1rem',
    marginBottom: '1rem'
  });

  const iconStyle = (process) => ({
    width: '3rem',
    height: '3rem',
    borderRadius: '0.75rem',
    background: process.bgColor,
    border: `2px solid ${process.borderColor}`,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '1.25rem',
    fontWeight: '700',
    color: process.color,
    flexShrink: 0
  });

  const cardTitleStyle = {
    fontSize: '1.25rem',
    fontWeight: '600',
    color: '#111827',
    margin: 0
  };

  const cardDescriptionStyle = {
    fontSize: '0.95rem',
    color: '#6b7280',
    lineHeight: '1.6',
    margin: 0
  };

  const decorativeElementStyle = (process) => ({
    position: 'absolute',
    top: '-2rem',
    right: '-2rem',
    width: '4rem',
    height: '4rem',
    background: `linear-gradient(135deg, ${process.color}20, ${process.color}10)`,
    borderRadius: '50%',
    opacity: 0.5
  });

  const benefitsStyle = {
    marginTop: '4rem',
    textAlign: 'center',
    padding: '3rem',
    background: 'rgba(248, 250, 252, 0.6)',
    backdropFilter: 'blur(10px)',
    borderRadius: '1.5rem',
    border: '1px solid rgba(226, 232, 240, 0.5)'
  };

  const benefitsGridStyle = {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
    gap: '2rem',
    marginTop: '2rem'
  };

  const benefitItemStyle = {
    textAlign: 'center'
  };

  const benefitNumberStyle = {
    fontSize: '2.5rem',
    fontWeight: '700',
    background: 'linear-gradient(135deg, #3b82f6, #8b5cf6)',
    WebkitBackgroundClip: 'text',
    WebkitTextFillColor: 'transparent',
    backgroundClip: 'text',
    marginBottom: '0.5rem'
  };

  const benefitTextStyle = {
    fontSize: '0.95rem',
    color: '#6b7280',
    fontWeight: '500'
  };

  return (
    <div style={containerStyle}>
      {/* Responsive Grid CSS */}
      <style>{`
        .automation-grid {
          display: grid;
          grid-template-columns: repeat(3, 1fr);
          gap: 1.5rem;
          margin-top: 2rem;
        }
        @media (max-width: 1024px) {
          .automation-grid {
            grid-template-columns: repeat(2, 1fr);
          }
        }
        @media (max-width: 640px) {
          .automation-grid {
            grid-template-columns: 1fr;
          }
        }
      `}</style>

      <div style={innerContainerStyle}>
        {/* Header */}
        <div style={headerStyle}>
          <h2 style={titleStyle}>
            Comprehensive Dental Automation Bots for Revenue Cycle Management
          </h2>
          <p style={subtitleStyle}>
            Streamline your dental practice operations with our AI-powered automation bots designed to handle every aspect of 
            <strong> healthcare revenue cycle management services</strong>. Our <strong>medical billing outsourcing services</strong> deliver 
            exceptional results for dental practices, establishing us as one of the <strong>best revenue cycle management companies</strong> and 
            leading <strong>revenue cycle management services companies</strong>.
          </p>
        </div>

        {/* Automation Process Cards - 3x3 Grid */}
        <div className="automation-grid">
          {automationProcesses.map((process, index) => (
            <article
              key={process.id}
              style={getCardStyle(index)}
              onMouseEnter={() => setHoveredCard(index)}
              onMouseLeave={() => setHoveredCard(null)}
            >
              <div style={decorativeElementStyle(process)}></div>
              
              <div style={cardHeaderStyle(process)}>
                <div style={iconStyle(process)}>
                  {process.id}
                </div>
                <h3 style={cardTitleStyle}>{process.title}</h3>
              </div>
              
              <p style={cardDescriptionStyle}>
                {process.description}
              </p>
            </article>
          ))}
        </div>

        {/* Benefits Section with Keywords */}
        <div style={benefitsStyle}>
          <h2 style={{
            fontSize: '2rem',
            fontWeight: '700',
            color: '#111827',
            marginBottom: '1rem'
          }}>
            Why Choose MDeRCM Dental Automation?
          </h2>
          <p style={{
            fontSize: '1.125rem',
            color: '#6b7280',
            marginBottom: '2rem',
            maxWidth: '850px',
            margin: '0 auto 2rem auto',
            lineHeight: '1.7'
          }}>
            Transform your dental practice with our <strong>outsourced revenue cycle management services</strong>. 
            Our <strong>revenue cycle management consulting services</strong> help you learn 
            <strong> how to improve revenue cycle management</strong> with proven <strong>healthcare cost reduction strategies</strong>. 
            Join leading <strong>medical billing outsourcing companies</strong> that trust <Link to="/" style={{ color: '#F26522', textDecoration: 'none', fontWeight: '600' }}>MDeRCM</Link> for 
            <strong> hospital revenue cycle management</strong> excellence.
          </p>

          <div style={benefitsGridStyle}>
            <div style={benefitItemStyle}>
              <div style={benefitNumberStyle}>98%</div>
              <div style={benefitTextStyle}>Automation Rate</div>
            </div>
            <div style={benefitItemStyle}>
              <div style={benefitNumberStyle}>60%</div>
              <div style={benefitTextStyle}>Faster Processing</div>
            </div>
            <div style={benefitItemStyle}>
              <div style={benefitNumberStyle}>40%</div>
              <div style={benefitTextStyle}>Cost Reduction</div>
            </div>
            <div style={benefitItemStyle}>
              <div style={benefitNumberStyle}>99.9%</div>
              <div style={benefitTextStyle}>Accuracy Rate</div>
            </div>
          </div>
        </div>

        {/* Additional SEO Keywords Section */}
        <div style={{
          marginTop: '3rem',
          padding: '2.5rem',
          background: 'rgba(243, 244, 246, 0.5)',
          borderRadius: '1rem',
          textAlign: 'center'
        }}>
          <h3 style={{
            fontSize: '1.5rem',
            fontWeight: '700',
            color: '#111827',
            marginBottom: '1.5rem'
          }}>
            Comprehensive Revenue Cycle Management Services for Dental Practices
          </h3>
          <p style={{
            fontSize: '15px',
            lineHeight: '1.8',
            color: '#555',
            maxWidth: '900px',
            margin: '0 auto 1.5rem'
          }}>
            Our dental <strong>revenue cycle management services for hospitals</strong> and practices combine 
            <strong> medical billing and coding outsourcing</strong> expertise with AI automation. 
            As specialists among <strong>revenue cycle management services companies</strong>, we deliver 
            <strong> medical revenue cycle management services</strong> that reduce costs and improve collections. 
            Experience proven <strong>healthcare cost reduction consulting</strong> strategies from leading 
            <strong> revenue cycle management services USA</strong> providers.
          </p>
          <p style={{
            fontSize: '15px',
            lineHeight: '1.8',
            color: '#555',
            maxWidth: '900px',
            margin: '0 auto'
          }}>
            Discover why dental practices choose <Link to="/" style={{ color: '#F26522', textDecoration: 'none', fontWeight: '600' }}>MDeRCM</Link> as their trusted partner for 
            <strong> outsourced revenue cycle management services</strong>. Our comprehensive <strong>healthcare revenue cycle management services</strong> 
            include eligibility verification, claims management, denial prevention, and accounts receivable optimization—all powered by advanced AI technology.
          </p>
        </div>

        {/* Related Services Links */}
        <div style={{
          marginTop: '3rem',
          padding: '2.5rem',
          background: 'linear-gradient(135deg, rgba(59, 130, 246, 0.05), rgba(139, 92, 246, 0.05))',
          borderRadius: '1rem',
          textAlign: 'center'
        }}>
          <h3 style={{
            fontSize: '1.5rem',
            fontWeight: '700',
            color: '#111827',
            marginBottom: '1.5rem'
          }}>
            Explore Our Other Healthcare Solutions
          </h3>
          <div style={{
            display: 'flex',
            flexWrap: 'wrap',
            justifyContent: 'center',
            gap: '1rem',
            marginTop: '1.5rem'
          }}>
            <Link
              to="/healthcare/ai/accounts-receivable"
              style={{
                padding: '0.75rem 1.5rem',
                background: 'white',
                color: '#2563eb',
                textDecoration: 'none',
                borderRadius: '0.5rem',
                fontWeight: '600',
                fontSize: '0.95rem',
                boxShadow: '0 4px 12px rgba(0, 0, 0, 0.08)',
                transition: 'all 0.3s ease'
              }}
            >
              Accounts Receivable Management
            </Link>
            <Link
              to="/healthcare/ai/denial-management"
              style={{
                padding: '0.75rem 1.5rem',
                background: 'white',
                color: '#7c3aed',
                textDecoration: 'none',
                borderRadius: '0.5rem',
                fontWeight: '600',
                fontSize: '0.95rem',
                boxShadow: '0 4px 12px rgba(0, 0, 0, 0.08)',
                transition: 'all 0.3s ease'
              }}
            >
              Denial Management Services
            </Link>
            <Link
              to="/healthcare/ai/compliance-agent"
              style={{
                padding: '0.75rem 1.5rem',
                background: 'white',
                color: '#10b981',
                textDecoration: 'none',
                borderRadius: '0.5rem',
                fontWeight: '600',
                fontSize: '0.95rem',
                boxShadow: '0 4px 12px rgba(0, 0, 0, 0.08)',
                transition: 'all 0.3s ease'
              }}
            >
              Compliance Management
            </Link>
            <Link
              to="/healthcare/ai/payment-posting"
              style={{
                padding: '0.75rem 1.5rem',
                background: 'white',
                color: '#f97316',
                textDecoration: 'none',
                borderRadius: '0.5rem',
                fontWeight: '600',
                fontSize: '0.95rem',
                boxShadow: '0 4px 12px rgba(0, 0, 0, 0.08)',
                transition: 'all 0.3s ease'
              }}
            >
              Payment Posting Services
            </Link>
          </div>
          <p style={{
            marginTop: '1.5rem',
            fontSize: '0.95rem',
            color: '#6b7280'
          }}>
            All services powered by <Link to="/" style={{ color: '#F26522', textDecoration: 'none', fontWeight: '600' }}>MDeRCM</Link>'s advanced AI technology
          </p>
        </div>
      </div>
    </div>
  );
};

export default DentalAutomationSection;